import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, rgb } from 'pdf-lib';

// Worker is copied into public/ at build time (PWA-safe, no CDN).
pdfjsLib.GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.js';

const PEN = { color: '#e11d48', width: 3 };
// The highlighter (Ryan, Nov 2 2026, submittal drafts): a wide translucent yellow stroke that lets the
// text show through. Width is a share of the page so it prints the same at any zoom.
const HI = { color: '#facc15', widthShare: 0.02, alpha: 0.4 };

// Freehand markup viewer for a vendor-quote PDF or a submittal package draft: drag a red pen or a
// yellow highlighter over the pages, saved as an editable overlay (normalized 0..1 coords) and
// flattenable into a download. onPrint (optional) adds a button that hands printing back to the caller.
//
// Only the pages you are looking at are drawn. Rendering the whole document up front
// killed the tab on real quotes — a 41-page quote is 82 canvases at 850×1100, about
// 300MB of canvas, and the browser stops handing out memory partway through. That is
// why later pages came back blank and why the tool crashed.
export default function PdfAnnotator({ url, token, title, downloadName, initialAnnotations, onSave, onClose, onPrint = null, printLabel = '🖨 Print', hint = '' }) {
  const [pages, setPages] = useState([]);   // [{ width, height, page, vp }]
  const [tool, setToolState] = useState('pen');  // 'pen' | 'hi'
  const toolRef = useRef('pen');                 // read at mousedown, so a tool picked a moment ago always counts
  const setTool = t => { toolRef.current = t; setToolState(t); };
  const [strokes, setStrokes] = useState(() => {
    try {
      const a = typeof initialAnnotations === 'string' ? JSON.parse(initialAnnotations) : initialAnnotations;
      if (Array.isArray(a)) return a;
      if (a && Array.isArray(a.strokes)) return a.strokes;
    } catch {}
    return [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Which pages are near the viewport, and which are currently drawn.
  const [visible, setVisible] = useState(() => new Set([0]));
  const wrapRefs = useRef([]);          // page containers, watched by the observer
  const scrollRef = useRef(null);
  const renderTasks = useRef({});       // in-flight pdf.js renders, so they can be cancelled
  const renderedRef = useRef(new Set());

  const bytesRef = useRef(null);        // original PDF bytes (for flatten/download)
  const pageCanvasRefs = useRef([]);    // rendered PDF canvases
  const overlayRefs = useRef([]);       // drawing canvases
  const drawing = useRef(null);         // in-progress stroke

  const redraw = useCallback((idx) => {
    const ov = overlayRefs.current[idx];
    if (!ov) return;
    const ctx = ov.getContext('2d');
    ctx.clearRect(0, 0, ov.width, ov.height);
    const list = strokes.filter(s => s && s.page === idx);
    if (drawing.current && drawing.current.page === idx) list.push(drawing.current);
    list.forEach(s => {
      if (!s.points || s.points.length < 1) return;
      const hi = s.tool === 'hi';
      ctx.save();
      ctx.globalAlpha = hi ? HI.alpha : 1;
      ctx.globalCompositeOperation = hi ? 'multiply' : 'source-over';   // the words show through the yellow
      ctx.strokeStyle = hi ? HI.color : (s.color || PEN.color);
      ctx.lineWidth = hi ? Math.max(8, ov.width * HI.widthShare) : (s.width || PEN.width);
      ctx.lineCap = hi ? 'butt' : 'round'; ctx.lineJoin = 'round';
      ctx.beginPath();
      s.points.forEach((p, i) => { const x = p.x * ov.width, y = p.y * ov.height; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
      ctx.stroke();
      ctx.restore();
    });
  }, [strokes]);

  // Load the PDF once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(url, { headers: token ? { 'x-worker-token': token } : {} });
        if (!resp.ok) throw new Error('Could not load the PDF.');
        const buf = new Uint8Array(await resp.arrayBuffer());
        bytesRef.current = buf.slice(0);
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const meta = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = Math.min(2, 850 / base.width);
          const vp = page.getViewport({ scale });
          meta.push({ width: Math.floor(vp.width), height: Math.floor(vp.height), page, vp });
        }
        if (!cancelled) { setPages(meta); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message || 'Failed to load the PDF.'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [url, token]);

  // Which pages are near the viewport, worked out from the scroll position. This was
  // an IntersectionObserver first, but it never fires in some embedded browsers — and a
  // page that silently never draws is a worse bug than the one this replaces. Measuring
  // rectangles is dull and works everywhere.
  const NEAR = 800;   // keep a screenful either side drawn so scrolling shows no blanks
  const recompute = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc || !pages.length) return;
    const box = sc.getBoundingClientRect();
    const next = new Set();
    pages.forEach((_, idx) => {
      const el = wrapRefs.current[idx];
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom >= box.top - NEAR && r.top <= box.bottom + NEAR) next.add(idx);
    });
    setVisible(prev => (prev.size === next.size && [...next].every(i => prev.has(i))) ? prev : next);
  }, [pages]);

  useEffect(() => {
    recompute();
    const sc = scrollRef.current;
    if (!sc) return undefined;
    let last = 0;
    const onScroll = () => {
      // A short throttle on the clock, not on the frame — rAF is paused in a
      // background tab and the pages would never appear.
      const t = Date.now();
      if (t - last < 80) return;
      last = t;
      recompute();
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      sc.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [recompute]);

  // Draw what is near, and give back the memory for what is not.
  useEffect(() => {
    pages.forEach((m, idx) => {
      const cv = pageCanvasRefs.current[idx];
      const ov = overlayRefs.current[idx];
      if (!cv) return;
      if (visible.has(idx)) {
        if (renderedRef.current.has(idx)) return;
        renderedRef.current.add(idx);
        cv.width = m.width; cv.height = m.height;
        if (ov) { ov.width = m.width; ov.height = m.height; }
        try {
          const task = m.page.render({ canvasContext: cv.getContext('2d'), viewport: m.vp });
          renderTasks.current[idx] = task;
          task.promise.then(() => redraw(idx)).catch(() => {});
        } catch { renderedRef.current.delete(idx); }
      } else if (renderedRef.current.has(idx)) {
        renderedRef.current.delete(idx);
        try { renderTasks.current[idx] && renderTasks.current[idx].cancel(); } catch {}
        delete renderTasks.current[idx];
        // Zero-sizing a canvas is what actually frees its backing store.
        cv.width = 0; cv.height = 0;
        if (ov) { ov.width = 0; ov.height = 0; }
        try { m.page.cleanup(); } catch {}
      }
    });
  }, [visible, pages, redraw]);

  // Repaint overlays whenever the saved strokes change.
  useEffect(() => { renderedRef.current.forEach(idx => redraw(idx)); }, [strokes, redraw]);

  function point(e, idx) {
    const ov = overlayRefs.current[idx];
    const r = ov.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: Math.max(0, Math.min(1, cx / r.width)), y: Math.max(0, Math.min(1, cy / r.height)) };
  }
  function onDown(e, idx) { e.preventDefault(); drawing.current = { page: idx, tool: toolRef.current, points: [point(e, idx)], color: PEN.color, width: PEN.width }; }
  function onMove(e, idx) { if (!drawing.current || drawing.current.page !== idx) return; drawing.current.points.push(point(e, idx)); redraw(idx); }
  // Take the stroke out of the ref BEFORE queuing the state update: the updater runs later, by which
  // time the ref was already cleared (a null stroke then crashed redraw — Nov 2 2026).
  function onUp() { const s = drawing.current; drawing.current = null; if (s && s.points.length > 1) { setStrokes(list => [...list, s]); setDirty(true); } }

  function undo() { setStrokes(s => s.slice(0, -1)); setDirty(true); }
  function clearAll() { if (strokes.length && window.confirm('Clear all markup on this PDF?')) { setStrokes([]); setDirty(true); } }

  async function save() {
    setSaving(true);
    try { await onSave({ strokes }); setDirty(false); }
    catch (e) { window.alert('Could not save markup: ' + (e?.error || e?.message || 'error')); }
    finally { setSaving(false); }
  }

  async function download() {
    try {
      const doc = await PDFDocument.load(bytesRef.current);
      const pgs = doc.getPages();
      const red = rgb(0.88, 0.07, 0.28), yellow = rgb(0.98, 0.8, 0.08);
      strokes.forEach(s => {
        const pg = pgs[s.page]; if (!pg) return;
        const { width, height } = pg.getSize();
        const hi = s.tool === 'hi';
        for (let i = 1; i < s.points.length; i++) {
          const a = s.points[i - 1], b = s.points[i];
          pg.drawLine({ start: { x: a.x * width, y: (1 - a.y) * height }, end: { x: b.x * width, y: (1 - b.y) * height },
            thickness: hi ? Math.max(8, width * HI.widthShare) : 1.6, color: hi ? yellow : red, opacity: hi ? HI.alpha : 1 });
        }
      });
      const bytes = await doc.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = ((downloadName || 'quote').replace(/\.pdf$/i, '')) + ' (marked-up).pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { window.alert('Could not build the marked-up PDF: ' + (e?.message || 'error')); }
  }

  const btn = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });

  return (
    // Above the card windows (6000) and the PDF window (6500): opened from inside a submittal card (Nov 2 2026).
    <div style={{ position: 'fixed', inset: 0, zIndex: 7000, background: 'rgba(15,23,42,0.75)', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#111827', color: '#fff', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, fontSize: 15, flex: 1, minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          ✏️ {title || 'Markup'}
          <span style={{ marginLeft: 10, fontWeight: 600, fontSize: 12, color: '#cbd5e1' }}>{hint || 'Drag to draw'}</span>
        </div>
        {/* Tools: red pen or yellow highlighter (Ryan, Nov 2 2026). */}
        <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #4b5563' }}>
          <button onClick={() => setTool('pen')} title="Red pen" style={{ ...btn(tool === 'pen' ? '#e11d48' : '#1f2937', '#fff'), borderRadius: 0 }}>🖊 Pen</button>
          <button onClick={() => setTool('hi')} title="Yellow highlighter" style={{ ...btn(tool === 'hi' ? '#ca8a04' : '#1f2937', '#fff'), borderRadius: 0 }}>🖍 Highlight</button>
        </div>
        <button onClick={undo} disabled={!strokes.length} style={btn('#374151', strokes.length ? '#fff' : '#6b7280')}>↶ Undo</button>
        <button onClick={clearAll} disabled={!strokes.length} style={btn('#374151', strokes.length ? '#fff' : '#6b7280')}>Clear</button>
        {!onPrint && <button onClick={download} style={btn('#1f2937', '#fff')}>⬇ Download marked-up PDF</button>}
        <button onClick={save} disabled={saving} style={btn(dirty ? '#e87722' : '#16a34a', '#fff')}>{saving ? 'Saving…' : dirty ? 'Save markup' : 'Saved ✓'}</button>
        {onPrint && <button onClick={async () => { if (dirty) await save(); onPrint(); }} disabled={saving} title="Saves the markup, then prints the package with it burned in" style={btn('#1d4ed8', '#fff')}>{printLabel}</button>}
        <button onClick={onClose} style={btn('#dc2626', '#fff')}>✕ Close</button>
      </div>

      {/* Pages */}
      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        {loading && <div style={{ color: '#fff', marginTop: 40 }}>Loading PDF…</div>}
        {error && <div style={{ color: '#fecaca', background: '#7f1d1d', padding: '12px 18px', borderRadius: 8, marginTop: 40 }}>{error}</div>}
        {pages.length > 1 && (
          <div style={{ color: '#cbd5e1', fontSize: 12.5, fontWeight: 700 }}>{pages.length} pages — scroll to see them all</div>
        )}
        {pages.map((m, idx) => (
          // flexShrink matters: in a column flex box the pages would otherwise be squashed
          // to fit rather than overflowing into a scroll, which hid most of a long quote.
          <div key={idx} ref={el => (wrapRefs.current[idx] = el)} data-page={idx}
            style={{ position: 'relative', width: m.width, height: m.height, flexShrink: 0,
              boxShadow: '0 6px 24px rgba(0,0,0,0.4)', background: '#fff' }}>
            <canvas ref={el => (pageCanvasRefs.current[idx] = el)} style={{ position: 'absolute', inset: 0, width: m.width, height: m.height }} />
            <canvas ref={el => (overlayRefs.current[idx] = el)}
              style={{ position: 'absolute', inset: 0, width: m.width, height: m.height, cursor: 'crosshair', touchAction: 'none' }}
              onMouseDown={e => onDown(e, idx)} onMouseMove={e => onMove(e, idx)} onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={e => onDown(e, idx)} onTouchMove={e => onMove(e, idx)} onTouchEnd={onUp} />
          </div>
        ))}
      </div>
    </div>
  );
}
